import { useMutation, useQuery } from "@apollo/client";
import OpenInNew from "@mui/icons-material/OpenInNew";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Link from "@mui/material/Link";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useAlerts } from "components/Alerts/AlertProvider";
import { CopyableText } from "components/CopyableText";
import { InfoIconTooltip } from "components/InfoIconTooltip";
import { MultipleUserPicker, UserOption } from "components/MultipleUserPicker";
import { RightDrawerShell } from "components/RightDrawerShell/RightDrawerShell";
import {
  HarmonizedViolationAssigneeSource,
  HarmonizedViolationGovStatus,
  HarmonizedViolationResolutionStatus,
  Permission,
} from "generated-graphql/graphql";
import { useAuthorization } from "hooks/useAuthorization";
import { useCurrentUser } from "hooks/useCurrentUser";
import {
  HARMONIZED_VIOLATION_DEFAULT_ASSIGNEES,
  HARMONIZED_VIOLATION_STATS,
  HARMONIZED_VIOLATIONS,
  SET_HARMONIZED_VIOLATION_ASSIGNEE,
  SET_HARMONIZED_VIOLATION_FACILITY_DEFAULT_ASSIGNEE,
  SET_HARMONIZED_VIOLATION_FACILITY_DEFAULT_STAFF_ASSIGNEE,
  SET_HARMONIZED_VIOLATION_GOV_STATUS,
  SET_HARMONIZED_VIOLATION_STAFF_ASSIGNEE,
} from "hooks/useHarmonizedViolations";
import { ReactNode, useState } from "react";
import { Link as RouterLink } from "react-router-dom";

import { assigneeInheritanceCaption } from "./assigneeInheritance";
import { assigneeDisplayName, type ViolationRow } from "./columns";
import { ScopeDefaultButton } from "./ScopeDefaultButton";
import { ScopeDefaultButtons } from "./ScopeDefaultButtons";
import { SeverityProvenance } from "./SeverityProvenance";
import { useSetDefaultAssignee } from "./useSetDefaultAssignee";
import { ViolationCommentsSection } from "./ViolationCommentsSection";
import { toUserOption, type ViolationDefaultAssignees } from "./violationDefaultAssignees";
import {
  formatReportedStatus,
  formatViolationDate,
  GOV_STATUS_LABEL,
  GOV_STATUS_OPTIONS,
  isViolationStale,
  ProgramChip,
  REPORTED_STATUS_TOOLTIP,
  SectionHeading,
  StaleChip,
} from "./violationDisplay";
import { ViolationDocumentsSection } from "./ViolationDocumentsSection";

type ResolutionStatusValue = HarmonizedViolationGovStatus | "";

/** Narrows an arbitrary select value to a valid tracked-status enum (or null). */
function toResolutionStatus(value: string): HarmonizedViolationGovStatus | null {
  return Object.values(HarmonizedViolationGovStatus).find((s) => s === value) ?? null;
}

/**
 * Editable, user-controlled "Resolution status" — backed by the `govStatus`
 * field, which (unlike EPA's derived `standardizedResolutionStatus`) is
 * preserved across data refreshes, so a user's "Resolved" sticks. EPA's own
 * derived status is shown beside it as read-only context.
 *
 * Mounted with `key={violation.id}` so its local state resets cleanly when a
 * different violation opens (no prop-sync effect). The mutation returns
 * { id, govStatus }; Apollo normalizes by id, so the grid row updates from cache.
 */
function ResolutionStatusEditor({
  violationId,
  initial,
  epaStatus,
  violationEndDate,
  lastSeenAt,
  tenantId,
}: {
  violationId: string;
  initial: ResolutionStatusValue;
  epaStatus: HarmonizedViolationResolutionStatus;
  violationEndDate: string | null | undefined;
  lastSeenAt: string | null | undefined;
  tenantId: string;
}) {
  const alerts = useAlerts();
  const [value, setValue] = useState<ResolutionStatusValue>(initial);
  const [setGovStatusMutation, { loading: saving, error: saveError }] = useMutation(
    SET_HARMONIZED_VIOLATION_GOV_STATUS
  );

  const handleChange = async (raw: string) => {
    const next = toResolutionStatus(raw);
    const previous = value;
    setValue(next ?? "");
    try {
      await setGovStatusMutation({ variables: { tenantId, id: violationId, govStatus: next } });
      alerts.success("Resolution status updated");
    } catch (e) {
      // Roll the field back to its last-saved value so it doesn't show a change
      // that didn't persist.
      setValue(previous);
      alerts.error("Could not save the resolution status. Try again.", e);
    }
  };

  return (
    <Stack gap={0.5}>
      <TextField
        select
        fullWidth
        size="small"
        label="Resolution status"
        value={value}
        disabled={saving}
        error={Boolean(saveError)}
        helperText={saveError ? "Could not save the resolution status. Try again." : undefined}
        onChange={(e) => void handleChange(e.target.value)}
      >
        <MenuItem value="">—</MenuItem>
        {GOV_STATUS_OPTIONS.map((status) => (
          <MenuItem key={status} value={status}>
            {GOV_STATUS_LABEL[status]}
          </MenuItem>
        ))}
      </TextField>
      {/*
        EPA's own status gets its own row rather than the TextField's helperText:
        a save error would otherwise replace it, and FormHelperText renders a <p>
        that can't legally contain the tooltip's IconButton.
      */}
      <Stack direction="row" alignItems="center" gap={0.25}>
        <Typography variant="caption" color="text.secondary">
          Reported status: {formatReportedStatus(epaStatus, violationEndDate, lastSeenAt)}
        </Typography>
        <InfoIconTooltip
          tooltip={REPORTED_STATUS_TOOLTIP}
          ariaLabel="About reported status"
          iconButtonSx={{ color: (t) => t.palette.grey[400] }}
        />
      </Stack>
    </Stack>
  );
}

/** First selected user id from the single-select picker, or null when cleared. */
function firstUserId(users: UserOption[] | null): string | null {
  return users && users.length > 0 ? (users[0]?.id ?? null) : null;
}

/**
 * Mounted with `key={violation.id}` by the drawer so picker state resets when a
 * different violation opens (mirrors GovStatusEditor's keyed-no-effect pattern).
 *
 * The "set default" popovers carry their own selection, so setting a scope
 * default never writes an override as a side effect.
 */
function AssigneeEditor({
  violation,
  tenantId,
  defaults,
}: {
  violation: ViolationRow;
  tenantId: string;
  /** Null while the current defaults are loading; the popovers wait for them. */
  defaults: ViolationDefaultAssignees | null;
}) {
  // Only a real OVERRIDE belongs here, or the picker would pre-fill an inherited
  // assignee that reads as an override the user never set.
  const initialOverride: UserOption[] | null =
    violation.assignee && violation.assigneeSource === HarmonizedViolationAssigneeSource.Override
      ? [
          {
            id: violation.assignee.id,
            email: violation.assignee.email,
            name: assigneeDisplayName(violation.assignee),
          },
        ]
      : null;

  const alerts = useAlerts();
  const setDefaultAssignee = useSetDefaultAssignee();
  const { isStaff } = useCurrentUser();
  const { hasPermissions } = useAuthorization();
  // The API gates the business-unit and region mutations on staff or
  // WriteAllFacility, so hide those buttons for anyone else rather than offer a
  // save the server will reject. The per-facility default is not gated this way.
  const canSetScopeDefault = isStaff || hasPermissions([Permission.WriteAllFacility]);
  const [override, setOverride] = useState<UserOption[] | null>(initialOverride);

  const [setAssignee, { loading: savingOverride }] = useMutation(SET_HARMONIZED_VIOLATION_ASSIGNEE);
  const [setFacilityDefault, { loading: savingDefault }] = useMutation(
    SET_HARMONIZED_VIOLATION_FACILITY_DEFAULT_ASSIGNEE,
    {
      // Apollo can't infer which grid rows, donut buckets, or popover prefills
      // a default change touches, so refetch all three.
      refetchQueries: [
        HARMONIZED_VIOLATIONS,
        HARMONIZED_VIOLATION_STATS,
        HARMONIZED_VIOLATION_DEFAULT_ASSIGNEES,
      ],
      awaitRefetchQueries: true,
    }
  );

  const handleOverrideChange = async (users: UserOption[] | null) => {
    setOverride(users);
    try {
      await setAssignee({ variables: { tenantId, id: violation.id, userId: firstUserId(users) } });
      alerts.success(firstUserId(users) ? "Assignee updated" : "Assignee override cleared");
    } catch (e) {
      alerts.error("Could not save the assignee. Try again.", e);
    }
  };

  const saveFacilityDefault = (userId: string | null) =>
    setDefaultAssignee({
      run: async () => {
        const { data } = await setFacilityDefault({
          variables: { tenantId, facilityId: violation.facility.id, userId },
        });
        // Clearing answers with a null payload by design, so only a SAVE reads
        // null as the server declining.
        return userId ? Boolean(data?.setHarmonizedViolationFacilityDefaultAssignee) : true;
      },
      successMessage: userId
        ? `Default assignee set for ${violation.facility.name}`
        : `Default assignee cleared for ${violation.facility.name}`,
      errorMessage: userId
        ? "Could not set the facility default assignee. Try again."
        : "Could not clear the facility default assignee. Try again.",
    });

  // No assignee means nothing was inherited to name, whatever the source says,
  // so the helper is handed null rather than an "Unassigned" placeholder.
  const effectiveName = violation.assignee ? assigneeDisplayName(violation.assignee) : null;

  return (
    <Stack spacing={1} sx={{ mb: 2 }}>
      <Typography variant="subtitle2">Assignee</Typography>
      <MultipleUserPicker
        label="Assignee (override)"
        multiple={false}
        value={override}
        disabled={savingOverride}
        onChange={(users) => void handleOverrideChange(users)}
      />
      <Typography variant="caption" color="text.secondary">
        {assigneeInheritanceCaption(violation.assigneeSource, effectiveName)}
      </Typography>
      <ScopeDefaultButton
        buttonText={`Set default for ${violation.facility.name}`}
        pickerLabel="Default assignee"
        currentDefault={toUserOption(defaults?.facility?.user)}
        saving={savingDefault}
        disabled={!defaults}
        onSave={(userId) => saveFacilityDefault(userId)}
        onClear={() => saveFacilityDefault(null)}
      />
      <ScopeDefaultButtons
        violation={violation}
        tenantId={tenantId}
        canSetScopeDefault={canSetScopeDefault}
        defaults={defaults}
      />
    </Stack>
  );
}

/**
 * Staff-only counterpart to {@link AssigneeEditor}: a single-select STAFF user
 * picker for the per-violation staff OVERRIDE plus a "set as staff default"
 * control. Backed by `staffUsers` (Encamp employees) via the picker's
 * `staffOnly` mode — the backend mutations reject non-staff target users.
 *
 * Mounted with `key={violation.id}` by the drawer so picker state resets when a
 * different violation opens. The per-violation mutation returns
 * { id, staffAssignee, staffAssigneeIsFacilityDefault } so Apollo normalizes the
 * row by id; the facility-default mutation can change OTHER inherited rows, so
 * it refetches the list query. Rendered only for staff callers.
 */
function StaffAssigneeEditor({
  violation,
  tenantId,
  defaults,
}: {
  violation: ViolationRow;
  tenantId: string;
  /** Null while the current defaults are loading; the popover waits for them. */
  defaults: ViolationDefaultAssignees | null;
}) {
  const initialOverride: UserOption[] | null =
    violation.staffAssignee && !violation.staffAssigneeIsFacilityDefault
      ? [
          {
            id: violation.staffAssignee.id,
            email: violation.staffAssignee.email,
            name: assigneeDisplayName(violation.staffAssignee),
          },
        ]
      : null;

  const alerts = useAlerts();
  const setDefaultAssignee = useSetDefaultAssignee();
  const [override, setOverride] = useState<UserOption[] | null>(initialOverride);

  const [setStaffAssignee, { loading: savingOverride }] = useMutation(
    SET_HARMONIZED_VIOLATION_STAFF_ASSIGNEE
  );
  const [setFacilityDefault, { loading: savingDefault }] = useMutation(
    SET_HARMONIZED_VIOLATION_FACILITY_DEFAULT_STAFF_ASSIGNEE,
    {
      // Apollo can't infer which inherited grid rows or popover prefills a
      // default change touches, so refetch both.
      refetchQueries: [HARMONIZED_VIOLATIONS, HARMONIZED_VIOLATION_DEFAULT_ASSIGNEES],
      awaitRefetchQueries: true,
    }
  );

  const handleOverrideChange = async (users: UserOption[] | null) => {
    setOverride(users);
    try {
      await setStaffAssignee({
        variables: { tenantId, id: violation.id, userId: firstUserId(users) },
      });
      alerts.success(
        firstUserId(users) ? "Staff assignee updated" : "Staff assignee override cleared"
      );
    } catch (e) {
      alerts.error("Could not save the staff assignee. Try again.", e);
    }
  };

  const saveStaffDefault = (userId: string | null) =>
    setDefaultAssignee({
      run: async () => {
        const { data } = await setFacilityDefault({
          variables: { tenantId, facilityId: violation.facility.id, userId },
        });
        // Clearing answers with a null payload by design, so only a SAVE reads
        // null as the server declining.
        return userId ? Boolean(data?.setHarmonizedViolationFacilityDefaultStaffAssignee) : true;
      },
      successMessage: userId
        ? `Default staff assignee set for ${violation.facility.name}`
        : `Default staff assignee cleared for ${violation.facility.name}`,
      errorMessage: userId
        ? "Could not set the facility default staff assignee. Try again."
        : "Could not clear the facility default staff assignee. Try again.",
    });

  const effectiveLabel = violation.staffAssignee
    ? assigneeDisplayName(violation.staffAssignee)
    : "Unassigned";

  return (
    <Stack spacing={1} sx={{ mb: 2 }}>
      <Typography variant="subtitle2">Staff assignee</Typography>
      <MultipleUserPicker
        label="Staff assignee (override)"
        multiple={false}
        staffOnly
        value={override}
        disabled={savingOverride}
        onChange={(users) => void handleOverrideChange(users)}
      />
      <Typography variant="caption" color="text.secondary">
        {violation.staffAssigneeIsFacilityDefault
          ? `Inherited from the facility staff default: ${effectiveLabel}`
          : `Effective staff assignee: ${effectiveLabel}`}
      </Typography>
      <ScopeDefaultButton
        buttonText={`Set staff default for ${violation.facility.name}`}
        pickerLabel="Default staff assignee"
        currentDefault={toUserOption(defaults?.facilityStaff?.user)}
        staffOnly
        saving={savingDefault}
        disabled={!defaults}
        onSave={(userId) => saveStaffDefault(userId)}
        onClear={() => saveStaffDefault(null)}
      />
    </Stack>
  );
}

function Field({ label, value }: { label: string; value: ReactNode }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <Stack spacing={0.25}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" component="div">
        {value}
      </Typography>
    </Stack>
  );
}

/**
 * The read-only EPA summary: a two-column grid of fields (Field returns null
 * for empty values, so gaps close up), the co-located sites list, and the two
 * external ECHO report links. Facility identity leads.
 */
function ViolationDetailsSection({
  violation,
  tenantId,
}: {
  violation: ViolationRow;
  tenantId: string;
}) {
  return (
    <Stack spacing={2} sx={{ mt: 1, mb: 3 }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          columnGap: 3,
          rowGap: 2,
        }}
      >
        <Field
          label="EPA facility ID"
          value={
            violation.epaFacilityId ? <CopyableText>{violation.epaFacilityId}</CopyableText> : null
          }
        />
        <Field
          label="Registry ID"
          value={violation.registryId ? <CopyableText>{violation.registryId}</CopyableText> : null}
        />
        <Field label="State" value={violation.state} />
        <Field label="Agency" value={violation.violationAgency} />
        <Field label="Agency level" value={violation.violationAgencyLevel} />
        <Field label="Violation code" value={violation.violationCode} />
        <Field label="Pollutant" value={violation.pollutantTypeDesc} />
        <Field label="Citations" value={violation.citations} />
        <Field label="Citation descriptions" value={violation.citationDescs} />
        <Field label="Resolution method" value={violation.resolutionMethodDesc} />
        <Field label="Schedule event" value={violation.scheduleEventDesc} />
        <Field label="Started" value={formatViolationDate(violation.violationStartDate)} />
        <Field label="Ended" value={formatViolationDate(violation.violationEndDate)} />
        <Field label="Last seen" value={formatViolationDate(violation.lastSeenAt)} />
      </Box>

      {violation.siteCount > 1 ? (
        <Stack spacing={0.5}>
          <Typography variant="subtitle2">Affected sites ({violation.siteCount})</Typography>
          {violation.affectedFacilities.map((f) => (
            <Link
              key={f.id}
              component={RouterLink}
              to={`/o/${tenantId}/facilities/${f.id}/profile`}
              target="_blank"
              rel="noopener"
              variant="body2"
              sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
            >
              {f.name} <OpenInNew fontSize="inherit" />
            </Link>
          ))}
        </Stack>
      ) : null}

      <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1 }}>
        {violation.detailLink ? (
          <Button
            component="a"
            href={violation.detailLink}
            target="_blank"
            rel="noopener noreferrer"
            size="small"
            variant="outlined"
            endIcon={<OpenInNew />}
          >
            Violation Report in Echo
          </Button>
        ) : null}
        {violation.epaFacilityLink ? (
          <Button
            component="a"
            href={violation.epaFacilityLink}
            target="_blank"
            rel="noopener noreferrer"
            size="small"
            variant="outlined"
            endIcon={<OpenInNew />}
          >
            Facility Report in Echo
          </Button>
        ) : null}
      </Stack>
    </Stack>
  );
}

export function ViolationDetailDrawer({
  violation,
  tenantId,
  refreshStartedAt,
  onClose,
}: {
  violation: ViolationRow | null;
  tenantId: string;
  /** Start of the latest successful refresh — used to flag a stale violation. */
  refreshStartedAt?: string | null;
  onClose: () => void;
}) {
  const { isStaff } = useCurrentUser();
  // One fetch of the facility's current scope defaults, threaded into every
  // "set default" popover as its prefill.
  const { data: defaultsData } = useQuery(HARMONIZED_VIOLATION_DEFAULT_ASSIGNEES, {
    variables: { tenantId, facilityId: violation?.facility.id ?? "" },
    skip: !violation,
  });
  if (!violation) {
    // No violation selected: render nothing. A closed RightDrawerShell would
    // still leave a drawer paper in the DOM, because the shell sets
    // ModalProps={{ keepMounted: true }}.
    return null;
  }
  const defaults = defaultsData?.harmonizedViolationDefaultAssignees ?? null;

  const stale = isViolationStale(violation.lastSeenAt, refreshStartedAt);
  const facilityProfilePath = `/o/${tenantId}/facilities/${violation.facility.id}/profile`;

  return (
    <RightDrawerShell
      open
      onClose={onClose}
      // Widened over the default so the two-column details grid breathes.
      width={{ md: 800 }}
      title={
        <Typography variant="h6">
          Violation{" "}
          <Typography component="span" variant="inherit" fontWeight="bold">
            {violation.violationId}
          </Typography>
        </Typography>
      }
    >
      <Box role="region" aria-label="Violation detail">
        {/* Violation title and facility read as subheads with consistent type —
            they are the two things a reader orients on. */}
        <Box sx={{ mb: 2 }}>
          {violation.violationDescription ? (
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {violation.violationDescription}
            </Typography>
          ) : null}
          <Link
            component={RouterLink}
            to={facilityProfilePath}
            target="_blank"
            rel="noopener"
            variant="subtitle1"
            sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, fontWeight: 600 }}
          >
            {violation.facility.name} <OpenInNew fontSize="inherit" />
          </Link>
        </Box>

        {/* useFlexGap makes spacing a real CSS gap on both axes — without it a
            row Stack only spaces horizontally, so the wide escalation chips
            sat flush against the chip row above them whenever they wrapped. */}
        <Stack
          direction="row"
          spacing={0.5}
          useFlexGap
          alignItems="center"
          sx={{ mb: 2, flexWrap: "wrap", rowGap: 1 }}
        >
          {/* Each chip carries its own hover explanation, so there are no separate
              info icons in this row — the chip is the hover target. */}
          <ProgramChip program={violation.sourceProgram} />
          {stale ? <StaleChip /> : null}
        </Stack>

        {/* Everything about "why this severity" lives in one block — level,
            ranked reasons, and the old-signal caution — replacing the chip
            tooltip, escalation chips, and Permit status since field. */}
        <Box sx={{ mb: 3 }}>
          <SeverityProvenance
            severity={violation.standardizedSeverity}
            determinantDesc={violation.stdSeverityDeterminantDesc}
            determinantCode={violation.stdSeverityDeterminantCode}
            orderStatuteCode={violation.orderStatuteCode}
            permitTerminalDate={violation.permitTerminalDate}
            now={new Date()}
          />
        </Box>

        <SectionHeading>Details</SectionHeading>
        <ViolationDetailsSection violation={violation} tenantId={tenantId} />

        <SectionHeading>Tracking</SectionHeading>
        <Stack spacing={2} sx={{ mt: 1, mb: 3 }}>
          <ResolutionStatusEditor
            key={`status-${violation.id}`}
            violationId={violation.id}
            initial={violation.govStatus ?? ""}
            epaStatus={violation.standardizedResolutionStatus}
            violationEndDate={violation.violationEndDate}
            lastSeenAt={violation.lastSeenAt}
            tenantId={tenantId}
          />
          <AssigneeEditor
            key={`assignee-${violation.id}`}
            violation={violation}
            tenantId={tenantId}
            defaults={defaults}
          />
          {isStaff ? (
            <StaffAssigneeEditor
              key={`staff-assignee-${violation.id}`}
              violation={violation}
              tenantId={tenantId}
              defaults={defaults}
            />
          ) : null}
          {violation.lastEditedBy ? (
            <Typography variant="caption" color="text.secondary">
              Last edited by {assigneeDisplayName(violation.lastEditedBy)} on{" "}
              {formatViolationDate(violation.lastEditedAt)}
            </Typography>
          ) : null}
        </Stack>

        <ViolationCommentsSection
          key={`comments-${violation.id}`}
          tenantId={tenantId}
          violationId={violation.id}
          facilityId={violation.facility.id}
        />

        <ViolationDocumentsSection
          key={`documents-${violation.id}`}
          tenantId={tenantId}
          violationId={violation.id}
          facilityId={violation.facility.id}
        />
      </Box>
    </RightDrawerShell>
  );
}
